// Timezone utilities for user preference management

// Get user's detected timezone
export const getDetectedTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Common timezone groups for better UX
export const timezoneGroups = {
  'North America': [
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Phoenix', label: 'Arizona Time (Phoenix)' },
    { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  ],
  'Europe': [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
    { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
    { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
    { value: 'Europe/Rome', label: 'Central European Time (Rome)' },
    { value: 'Europe/Madrid', label: 'Central European Time (Madrid)' },
    { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)' },
    { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)' },
    { value: 'Europe/Athens', label: 'Eastern European Time (Athens)' },
    { value: 'Europe/Moscow', label: 'Moscow Time (Moscow)' },
  ],
  'Asia': [
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (Hong Kong)' },
    { value: 'Asia/Singapore', label: 'Singapore Time (Singapore)' },
    { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (Kolkata)' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
    { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)' },
    { value: 'Asia/Manila', label: 'Philippines Time (Manila)' },
  ],
  'Australia & Pacific': [
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
    { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)' },
    { value: 'Australia/Perth', label: 'Australian Western Time (Perth)' },
    { value: 'Australia/Adelaide', label: 'Australian Central Time (Adelaide)' },
    { value: 'Pacific/Auckland', label: 'New Zealand Time (Auckland)' },
    { value: 'Pacific/Fiji', label: 'Fiji Time (Suva)' },
  ],
  'South America': [
    { value: 'America/Sao_Paulo', label: 'Brasília Time (São Paulo)' },
    { value: 'America/Buenos_Aires', label: 'Argentina Time (Buenos Aires)' },
    { value: 'America/Lima', label: 'Peru Time (Lima)' },
    { value: 'America/Bogota', label: 'Colombia Time (Bogotá)' },
    { value: 'America/Santiago', label: 'Chile Time (Santiago)' },
  ],
  'Africa': [
    { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)' },
    { value: 'Africa/Johannesburg', label: 'South Africa Time (Johannesburg)' },
    { value: 'Africa/Lagos', label: 'West Africa Time (Lagos)' },
    { value: 'Africa/Nairobi', label: 'East Africa Time (Nairobi)' },
  ]
}

// Flatten timezones for search
export const getAllTimezones = () => {
  const all = []
  Object.entries(timezoneGroups).forEach(([group, zones]) => {
    zones.forEach(zone => {
      all.push({ ...zone, group })
    })
  })
  return all
}

// Search timezones
export const searchTimezones = (query) => {
  if (!query) return getAllTimezones()
  
  const searchTerm = query.toLowerCase()
  return getAllTimezones().filter(tz => 
    tz.label.toLowerCase().includes(searchTerm) ||
    tz.value.toLowerCase().includes(searchTerm) ||
    tz.group.toLowerCase().includes(searchTerm)
  )
}

// Format timezone for display
export const formatTimezone = (timezone) => {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    const time = formatter.format(now)
    
    const tzInfo = getAllTimezones().find(tz => tz.value === timezone)
    const label = tzInfo ? tzInfo.label : timezone
    
    return `${label} (${time})`
  } catch (error) {
    return timezone
  }
}

// Get timezone offset
export const getTimezoneOffset = (timezone) => {
  try {
    const now = new Date()
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000))
    const local = new Date(utc.toLocaleString('en-US', { timeZone: timezone }))
    const diff = (local.getTime() - utc.getTime()) / (1000 * 60 * 60)

    const sign = diff > 0 ? '+' : diff < 0 ? '' : '+'

    // Format without unnecessary decimals
    if (diff % 1 === 0) {
      // Whole number (e.g., -8, +5)
      return `UTC${sign}${Math.round(diff)}`
    } else {
      // Has decimal (e.g., +5.5, +9.5)
      return `UTC${sign}${diff.toFixed(1)}`
    }
  } catch (error) {
    return 'UTC+0'
  }
}

// Validate timezone
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch (error) {
    return false
  }
}
